import * as React from 'react';
import {
  PluginConfig,
  ProductSearchResultItem,
  SearchResultItem,
  SiteSearchConfig, UmbracoSearchResultItem,
  VehicleSearchResultItem
} from "../../empracoTypes";
import {SearchType} from "./site-search-request";
import {FaIcon} from "../../generic/icon";
import {useCurrentSiteSearchContext} from "./site-search-common";

function MissingImage() {
  return <div className='empori-site-search-missing-image'>
    <FaIcon clsName='fa-regular fa-image' />
  </div>
}

function truncate(text: string, maxLength = 160) {
  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength - 3);
  const lastSpace = truncated.lastIndexOf(' ');

  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '...';
}

interface ProductResultItemProps {
  item: ProductSearchResultItem;
}

function ProductResultItem({item}: ProductResultItemProps) {
  const { primeflow } = useCurrentSiteSearchContext().config;
  const translations = primeflow?.translations;

  return <a href={item.url} className='empori-site-search-result-item element-flex'>
    {item.imagePreview
      ? <img src={item.imagePreview} alt={item.title} />
      : <MissingImage />}
    
    <div className='empori-site-search-result-item-content'>
      <span className='title'>
        {truncate(item.title, 35)}
      </span>

      {item.sku &&
        <small>({item.sku})</small>}
        
      {item.variants < 2 && (
        <>
          {item.status &&
            <>
              {item.status === "in_stock" && (
                <small>
                  <i className="fa-regular fa-box in-stock"></i>{' '}
                  {item.label || translations['SearchResultInStock'] || 'I lager'}
                </small>
              )}
              
              {item.status === "not_in_stock" && (
                <small>
                  <i className="fa-regular fa-box not-in-stock"></i>{' '}
                  {item.label || translations['SearchResultNotInStock'] || 'Ej i lager'}
                </small>
              )}
              
              {item.status === "in_supplier_stock" && (
                <small>
                  <i className="fa-regular fa-box supplier-stock"></i>{' '}
                  {item.label || translations['SearchResultSupplierStock'] || 'I leverantörs lager'}
                </small>
              )}
              
              {item.status === "out_of_stock" && (
                <small>
                  <i className="fa-regular fa-box out-of-stock"></i>{' '}
                  {item.label || translations['SearchResultOutOfStock'] || 'Slut i lager'}
                </small>
              )}
              
              {item.status === "unavailable" && (
                <small>
                  <i className="fa-regular fa-box unavailable"></i>{' '}
                  {item.label || translations['SearchResultUnavailableStock'] || 'Ej tillgängligt lager'}
                </small>
              )}
            </>
          }

          {!item.status && typeof item.inStock === 'boolean' && (
            <>
              {item.inStock ? (
                <small>
                  <i className="fa-regular fa-box in-stock"></i>{' '}
                  {item.label || translations['SearchResultInStock'] || 'I lager'}
                </small>
              ) : (
                <small>
                  <i className="fa-regular fa-box out-of-stock"></i>{' '}
                  {item.label || translations['SearchResultNotInStock'] || 'Ej i lager'}
                </small>
              )}
            </>
          )}

          <small className='price'>
            <strong>
              {item.price}
            </strong>
          </small>
        </>
      )}
      
      {item.variants > 1 && item.price &&
        <small className='price'>
          {primeflow?.translations['SearchResultFrom'] ?? 'Från'} <strong>{item.price}</strong>
        </small>
      }
    </div>
  </a>
}

interface OnlineShowroomItemProps {
  item: VehicleSearchResultItem;
}

function VehicleResultItem({item}: OnlineShowroomItemProps) {
  const onlineShowroom = useCurrentSiteSearchContext().config.onlineShowroom;
  const link = `${onlineShowroom.resultsPageUrl}?item=${item.friendlyId}`;
  const image = item.images?.[0]?.thumbnailUrl;
  
  return <a href={link} className='empori-site-search-result-item element-flex'>
    {image
      ? <img src={image} alt={item.title} />
      : <MissingImage />}

    <div className='empori-site-search-result-item-content'>
      <span className='title'>
        {truncate(item.title, 35)}
      </span>

      {item.filialBranch &&
        <small>{item.filialBranch}</small>}

      <small className='price'>
        <strong>{item.price.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })}</strong>
      </small>
    </div>
  </a>
}

interface UmbracoResultItemProps {
  item: UmbracoSearchResultItem;
}

function UmbracoResultItem({item}: UmbracoResultItemProps) {
  return <a href={item.url} className='empori-site-search-result-item element-flex'>
    {item.contentType === 'File' &&
      <FaIcon clsName='fas fa-file' />}

    <div className='empori-site-search-result-item-content'>
      <span className='title'>
        {item.title}
      </span>

      {item.description &&
        <small className='description'>
          {truncate(item.description)}
        </small>}
    </div>
  </a>
}

interface ResultItemProps {
  type: SearchType;
  item: SearchResultItem;
}

export function ResultItem({ item, type }: ResultItemProps) {  
  switch (type) {
    case SearchType.product:
      return <ProductResultItem item={item as ProductSearchResultItem} />;
    case SearchType.vehicle:
      return <VehicleResultItem item={item as VehicleSearchResultItem} />;
    default:
      return <UmbracoResultItem item={item as UmbracoSearchResultItem} />;
  }
}